import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes, FeatureFlagged } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

import { SignInProviderConfig } from '@backstage/core-components';
import { gitlabAuthApiRef } from '@backstage/core-plugin-api';

// plugin tech-radar
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';

// plugin home
import { HomepageCompositionRoot, VisitListener } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { CustomizableHomePage } from './components/home/CustomizableHomePage.tsx';

const gitlabProvider: SignInProviderConfig = {
  id: 'gitlab-auth-provider',
  title: 'GitLab',
  message: 'Sign in using GitLab',
  apiRef: gitlabAuthApiRef,
};

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => <SignInPage {...props} auto provider={gitlabProvider} />,
  },
});

const routes = (
  <FlatRoutes>

    <Route path="/" element={<Navigate to="home" />} />
    <FeatureFlagged with="customizable-home-page-preview">
      <Route path="/home" element={<HomepageCompositionRoot />}>
        <CustomizableHomePage />
      </Route>
    </FeatureFlagged>
    <FeatureFlagged without="customizable-home-page-preview">
      <Route path="/home" element={<HomepageCompositionRoot />}>
        <HomePage />
      </Route>
    </FeatureFlagged>

    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          columns={entityListContext =>
            CatalogTable.defaultColumnsFunc(entityListContext)
              .filter(
                c =>
                  c.field !== 'entity.metadata.description' &&
                  c.field !== 'entity.spec.lifecycle',
              )
              .map(c => {
                switch (c.field) {
                  case 'resolved.entityRef':
                    return {
                      ...c,
                      width: 'auto',
                      cellStyle: { minWidth: '260px', whiteSpace: 'nowrap' },
                      headerStyle: { minWidth: '260px', whiteSpace: 'nowrap' },
                    };
                  case 'resolved.partOfSystemRelationTitle':
                    return {
                      ...c,
                      width: '160px',
                      cellStyle: { whiteSpace: 'nowrap' },
                      headerStyle: { whiteSpace: 'nowrap' },
                    };
                  case 'resolved.ownedByRelationsTitle':
                    return {
                      ...c,
                      width: '160px',
                      cellStyle: { whiteSpace: 'nowrap' },
                      headerStyle: { whiteSpace: 'nowrap' },
                    };
                  case 'entity.spec.type':
                    return {
                      ...c,
                      width: '110px',
                      cellStyle: { whiteSpace: 'nowrap' },
                      headerStyle: { whiteSpace: 'nowrap' },
                    };
                  case 'entity.metadata.tags':
                    return {
                      ...c,
                      width: '200px',
                    };
                  default:
                    return c;
                }
              })
          }
        />
      }
    />
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route path="/docs/:namespace/:kind/:name/*" element={<TechDocsReaderPage />}>
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid />
      </TechDocsAddons>
    </Route>

    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/catalog-import" element={
      <RequirePermission permission={catalogEntityCreatePermission}>
        <CatalogImportPage />
      </RequirePermission>
    }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/tech-radar" element={<TechRadarPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />

  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <VisitListener />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);